/**
 * Outgoing voice signaling, paced. The server allows 40 messages per second with bursts of
 * 80 per connection, shared with the race traffic (~15 position updates a second). Joining
 * a room of eight means up to seven connections at once, each with an offer or answer and
 * a few ICE candidates, so signals go through a token bucket well below the limit:
 * descriptions first, candidates after them, and messages that became pointless while
 * waiting (the connection they belong to was replaced) are dropped.
 */
import type { PlayerId, RtcSignal } from '@cube-rush/shared';

export type SignalPriority = 'high' | 'low';

export interface SignalPacerOptions {
  /** Sustained rate, messages per second. */
  ratePerSecond: number;
  /** Bucket size: messages that may go out at once. */
  burst: number;
  /** Clock in ms (injected in tests). */
  now?: () => number;
}

export const DEFAULT_PACING: Readonly<SignalPacerOptions> = { ratePerSecond: 8, burst: 16 };

interface Pending {
  to: PlayerId;
  data: RtcSignal;
  /** True when the message is no longer worth sending. */
  stale?: () => boolean;
}

export class SignalPacer {
  private tokens: number;
  private updatedAt: number;
  private readonly high: Pending[] = [];
  private readonly low: Pending[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly now: () => number;

  constructor(
    private readonly send: (to: PlayerId, data: RtcSignal) => void,
    private readonly options: Readonly<SignalPacerOptions> = DEFAULT_PACING,
  ) {
    this.now = options.now ?? (() => Date.now());
    this.tokens = options.burst;
    this.updatedAt = this.now();
  }

  /** Messages waiting for their turn. */
  get pending(): number {
    return this.high.length + this.low.length;
  }

  enqueue(to: PlayerId, data: RtcSignal, priority: SignalPriority, stale?: () => boolean): void {
    const item: Pending = stale ? { to, data, stale } : { to, data };
    (priority === 'high' ? this.high : this.low).push(item);
    this.flush();
  }

  /** Forgets everything still waiting for `to` (the peer left or started over). */
  drop(to: PlayerId): void {
    for (const queue of [this.high, this.low]) {
      for (let i = queue.length - 1; i >= 0; i--) if (queue[i]?.to === to) queue.splice(i, 1);
    }
  }

  dispose(): void {
    this.high.length = 0;
    this.low.length = 0;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private flush(): void {
    this.refill();
    while (this.tokens >= 1) {
      const item = this.high.shift() ?? this.low.shift();
      if (!item) break;
      if (item.stale?.()) continue;
      this.tokens -= 1;
      this.send(item.to, item.data);
    }
    if (this.pending > 0 && this.timer === null) {
      const waitMs = Math.ceil(((1 - this.tokens) / this.options.ratePerSecond) * 1000);
      this.timer = setTimeout(
        () => {
          this.timer = null;
          this.flush();
        },
        Math.max(1, waitMs),
      );
    }
  }

  private refill(): void {
    const now = this.now();
    const elapsed = Math.max(0, now - this.updatedAt);
    this.updatedAt = now;
    this.tokens = Math.min(
      this.options.burst,
      this.tokens + (elapsed * this.options.ratePerSecond) / 1000,
    );
  }
}
